import React from "react";
import { ArrowRight, ShieldCheck, Clock, CheckCircle2, UserCheck, MailCheck } from "lucide-react";
import { Button } from "./ui/button";
import { heroStats, images } from "../mock";
import { GoogleG, Stars } from "./Testimonials";
import { useGoogleReviews } from "../hooks/useGoogleReviews";

const HeroGoogleBadge = () => {
  const { data } = useGoogleReviews();
  if (!data?.rating) return null;
  return (
    <a
      href="#reviews"
      data-testid="hero-google-badge"
      className="inline-flex items-center gap-2 bg-white border border-brand-100 rounded-full pl-2.5 pr-4 py-1.5 shadow-sm hover:border-brand-300 hover:shadow transition-all"
    >
      <GoogleG className="w-4 h-4" />
      <span className="font-display font-extrabold text-brand-900 text-[13px]">
        {data.rating.toFixed(1).replace(".", ",")}
      </span>
      <Stars n={data.rating} className="w-3.5 h-3.5" />
      <span className="text-[12px] text-brand-600">{data.user_rating_count} Bewertungen</span>
    </a>
  );
};

const Hero = () => {
  return (
    <section
      id="start"
      className="relative overflow-hidden bg-gradient-to-b from-brand-50 to-white"
    >
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-brand-100 rounded-full blur-3xl opacity-60" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-accent2-light rounded-full blur-3xl opacity-30" />

      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="inline-flex items-center gap-2 bg-white border border-brand-100 rounded-full px-4 py-1.5 text-[12px] font-medium text-brand-800 shadow-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                WEG-Verwaltung am Niederrhein: persönlich und zuverlässig
              </div>
              <HeroGoogleBadge />
            </div>

            <h1 data-testid="hero-headline" className="font-display text-[38px] md:text-[48px] lg:text-[54px] leading-[1.08] font-extrabold text-brand-900">
              Hausverwaltung in{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Kleve &amp; Umgebung</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-accent2/40 -z-0 rounded" />
              </span>
            </h1>

            <p data-testid="hero-subline" className="mt-4 text-lg md:text-xl font-medium text-brand-800 max-w-xl leading-snug">
              Persönliche und zuverlässige WEG-Verwaltung für Eigentümergemeinschaften
            </p>

            <h2 data-testid="hero-h2" className="mt-6 font-display text-base md:text-lg font-bold text-brand-900">
              Ihre Immobilie in besten Händen.
            </h2>
            <p className="mt-2 text-base text-brand-700 max-w-xl leading-relaxed">
              Transparent, kompetent und persönlich erreichbar. Zugelassen nach § 34c GewO. Wir verwalten
              nach den Vorgaben des Wohnungseigentumsgesetzes (§§ 19, 26 ff. WEG).
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                className="bg-brand-900 hover:bg-brand-800 text-white rounded-full h-14 px-7 text-[15px] shadow-lg shadow-brand-900/20"
              >
                <a href="#contact" className="inline-flex items-center gap-2">
                  Jetzt Beratungsgespräch vereinbaren
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full h-14 px-7 text-[15px] border-brand-200 text-brand-900 hover:bg-brand-50"
              >
                <a href="#services">Unsere Leistungen</a>
              </Button>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[13px] text-brand-700 max-w-xl">
              <div className="inline-flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                Erlaubnis nach § 34c GewO
              </div>
              <div className="inline-flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                100 % transparente Abrechnung
              </div>
              <div className="inline-flex items-start gap-2 sm:col-span-2" data-testid="hero-check-abrechnung">
                <Clock className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                Hausgeldabrechnung garantiert innerhalb von 10 Werktagen nach Eingang der Heizkostenabrechnung
              </div>
              <div className="inline-flex items-start gap-2">
                <UserCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                Ein fester, persönlicher Ansprechpartner
              </div>
              <div className="inline-flex items-start gap-2">
                <MailCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                Antwort auf E-Mails innerhalb von 24 Stunden
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-brand-900/20 aspect-[4/5] lg:aspect-[5/6]">
              <img
                src={images.hero}
                alt="Moderne Wohnimmobilie, Hausverwaltung in Kleve und Umgebung"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-900/40 to-transparent" />

              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-5 shadow-lg">
                <div className="grid grid-cols-3 divide-x divide-brand-100">
                  {heroStats.map((s) => (
                    <div key={s.label} className="px-2 text-center first:pl-0 last:pr-0">
                      <div className="font-display font-extrabold text-2xl md:text-3xl text-brand-900 count-num">
                        {s.value}
                      </div>
                      <div className="text-[11px] uppercase tracking-wider text-brand-500 mt-1">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="hidden md:flex absolute -top-6 -right-6 bg-white shadow-lg rounded-2xl p-4 pr-5 items-center gap-3 border border-brand-50">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <div className="font-semibold text-brand-900 text-sm">Aktuell Kapazitäten frei</div>
                <div className="text-[12px] text-brand-500">Für neue WEGs im Kreis Kleve und Umgebung</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
